import {type JSX} from "react";
import "./App.css";
import {AppBar, Box, Toolbar, Typography} from "@mui/material";
import {alpha, type Theme} from "@mui/material/styles";
import {Route, Routes, useLocation} from "react-router-dom";
import chatbotLogo from "./assets/images/Chatbot_logo_1024.png";
import AdminButton from "./components/admin/AdminButton";
import AdminDashboard from "./components/admin/AdminDashboard";
import {useAuth} from "./components/auth/AuthContext";
import Chat from "./components/chat/chat";
import AccessDeniedPage from "./components/error/AccessDeniedPage";
import CsrfInitializer from "./components/initializers/csrfInitializer";
import LocaleInitializer from "./components/initializers/localeInitializer";
import Login from "./components/login/login";
import ProtectedRoute from "./components/protectedRoute/protectedRoute";
import SettingsMenu from "./components/settings/SettingsMenu";
import {useTranslation} from "react-i18next";
import {adminDashboardPath, chatPath} from "./routes";
import {UserRole} from "./util/userRole";

const ProtectedApplicationRoute = ({element, requiredRole}: {element: JSX.Element; requiredRole?: UserRole}) => (
    <ProtectedRoute requiredRole={requiredRole}>
        <CsrfInitializer>
            <LocaleInitializer>{element}</LocaleInitializer>
        </CsrfInitializer>
    </ProtectedRoute>
);

export const getBannerSx = (theme: Theme) => ({
    zIndex: theme.zIndex.drawer + 1,
    color: theme.palette.mode === "dark" ? theme.palette.text.primary : theme.palette.primary.contrastText,
    background:
        theme.palette.mode === "dark"
            ? `linear-gradient(90deg, ${alpha(theme.palette.primary.dark, 0.92)} 0%, ${alpha(theme.palette.primary.main, 0.72)} 100%)`
            : theme.palette.primary.main,
    borderBottom: `1px solid ${
        theme.palette.mode === "dark"
            ? alpha(theme.palette.primary.light, 0.18)
            : alpha(theme.palette.primary.dark, 0.18)
    }`,
    boxShadow:
        theme.palette.mode === "dark"
            ? `0 10px 28px ${alpha(theme.palette.common.black, 0.3)}`
            : "none",
});

const Banner: React.FC = () => {
    const {t: translate} = useTranslation();
    const location = useLocation();
    const {isAdmin, isAuthenticated, status} = useAuth();
    const showControls = location.pathname !== "/login" && status === "authed" && isAuthenticated;

    return (
        <AppBar position="fixed" color="primary" elevation={0} enableColorOnDark sx={getBannerSx}>
            <Toolbar sx={{gap: 1.5, justifyContent: "space-between"}}>
                <Box sx={{display: "flex", alignItems: "center", gap: 1.5}}>
                    <Box
                        component="img"
                        src={chatbotLogo}
                        alt={translate("banner.logoAlt")}
                        sx={{height: 64, width: "auto"}}
                    />
                    <Typography variant="h6" component="div">
                        {translate("banner.title")}
                    </Typography>
                </Box>
                {showControls ? (
                    <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                        {isAdmin ? <AdminButton /> : null}
                        <SettingsMenu />
                    </Box>
                ) : null}
            </Toolbar>
        </AppBar>
    );
};

const App: React.FC = () => {
    return (
        <Box sx={{height: "100vh", display: "flex", flexDirection: "column", overflow: "hidden"}}>
            <link
                rel="stylesheet"
                href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Manrope:wght@600;700;800&display=swap"
            />
            <link rel="stylesheet" href="https://fonts.googleapis.com/icon?family=Material+Icons" />
            <Banner />
            <Toolbar sx={{flexShrink: 0}}/>
            <Box sx={{flexGrow: 1, minHeight: 0}}>
                <Routes>
                    <Route path="/login" element={<Login />} />
                    <Route path="/access-denied" element={<AccessDeniedPage />} />
                    <Route path="/" element={<ProtectedApplicationRoute element={<Chat />} />} />
                    <Route path={chatPath} element={<ProtectedApplicationRoute element={<Chat/>}/>}/>
                    <Route
                        path={adminDashboardPath}
                        element={<ProtectedApplicationRoute element={<AdminDashboard />} requiredRole={UserRole.ADMIN} />}
                    />
                </Routes>
            </Box>
        </Box>
    );
};

export default App;
